import { type VoidAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { LRUCache } from "@/cache/LRUCache";
import type { WmtsTile } from "@/cache/PVSTileService/TileGetters/WMTSTileGetter/WMTSTile";
import { tileGeometries, wrapIntoWorldZero } from "@/cache/SpatioTemporalTileCache/TileArea";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { RequestedTileGridOverlay } from "@/layers/debug/RequestedTileGridOverlay";
import { clearLayerLoading, reportLayerLoading } from "@/layers/geojson/layerLoadingStore";
import { getCurrentTileLayerTransitionRuntime } from "@/layers/png/tileLayerTransitionPolicy";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { isWmtsTileBoundariesDebugEnabled } from "@/map/mapDebugOptions";
import { WMTSRepository } from "@/repository/WMTSRepository";
import { safeFmt } from "@/utility/safeFmt";
import type { GuiTimeZone, WmsLayer } from "@mm/metx-workbench.meteomatics.com";
import { type InterpolationMode, getSpatialInterpolationMode } from "@mm/weather-parameter-utils";
import type { DateTime } from "luxon";
import type * as Mapbox from "mapbox-gl";
import { WMTSRenderer } from "./WMTSRenderer";
import { WMTSTileManager } from "./WMTSTileManager";
import type { WmtsDebugTile } from "./wmtsDebugGrid";

const DEFAULT_GPU_MEMORY_LIMIT = 512 * 1024 * 1024; // 512MB

export class WMTSLayerImpl extends LayerBase<WmsLayer> implements Mapbox.CustomLayerInterface {
  id: string;
  readonly type = "custom" as const;

  public interpolationMode: InterpolationMode;

  private renderer?: WMTSRenderer;
  private tileManager?: WMTSTileManager;
  private repository?: WMTSRepository;
  private publicSceneApi: ScenePublicApi;
  private lastMousePos?: { x: number; y: number };
  private shouldPreload = false;
  private isPending: ChangeDetector<boolean> = new ChangeDetector<boolean>(false);
  private readonly requestedTileGridOverlay: RequestedTileGridOverlay;

  constructor(uid: number, props: WmsLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(uid, props, scene, timezone);
    this.publicSceneApi = scene; // Assign before it might be downcast by super()

    // Technique: Standard-Matrix (Built-in Projection Matrix) + Texture-Sampling (raster tiles).
    this.id = this.humanReadableId();
    this.requestedTileGridOverlay = new RequestedTileGridOverlay(
      `${this.id}.wmts.debug-grid.source`,
      `${this.id}.wmts.debug-grid.layer`,
    );
    this.interpolationMode = getSpatialInterpolationMode(this.props.parameter_unit);
    const map = this.scene.getMapboxMap();
    map.addLayer(this);
    map.on("movestart", this.handleMapMoveStart);
    map.on("moveend", this.handleMapMoveEnd);
    document.addEventListener("mousemove", this.handleRawMouseMove);
  }

  get mapboxIndex(): string {
    return this.id;
  }

  humanReadableId(): string {
    return safeFmt`metx.WMTS.${this.props.model}:${this.props.parameter_unit}${
      this.props.ens_select ? `-${this.props.ens_select}` : ""
    }@${this.props.color_map}#${this.uid}`;
  }

  checker(): PropsChecker<WmsLayer, LayerBase<WmsLayer>> {
    const checkLayerProp = (name: any, signal?: (value: string) => void) => (prev: any, curr: any) => {
      const changed = prev[name] !== curr[name];
      if (changed) {
        this.setLayerProps({
          [name]: curr[name],
        });
      }
      return changed;
    };

    return {
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      parameter_unit: checkLayerProp("parameter_unit"),
      show: checkLayerProp("show"),
      calibrated: checkLayerProp("calibrated"),
      vertical_interpolation: checkLayerProp("vertical_interpolation"),
      color_map: checkLayerProp("color_map"),
      legend_visible: checkLayerProp("legend_visible"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  isRebuffering(time: DateTime): boolean {
    return this.tileManager?.isRebuffering(time) ?? false;
  }

  onAdd(map: Mapbox.Map, gl: WebGL2RenderingContext) {
    this.repository = new WMTSRepository(
      gl,
      new LRUCache<WmtsTile>((tile) => tile.memoryConsumptionGpu(), DEFAULT_GPU_MEMORY_LIMIT),
      () => {
        this.publicSceneApi.getMapboxMap().triggerRepaint();
      },
    );

    this.tileManager = new WMTSTileManager(this.repository, this.publicSceneApi, () => {
      const customInitDate =
        typeof this.props.custom_options?.init_date === "string" ? this.props.custom_options.init_date.trim() : "";
      const customOptions = this.props.custom_options as { api_url_suffix?: string } | undefined;
      const customApiUrlSuffix =
        typeof customOptions?.api_url_suffix === "string" ? customOptions.api_url_suffix.trim() : "";
      const transitionRuntime = getCurrentTileLayerTransitionRuntime();

      return {
        model: this.props.model,
        parameter_unit: this.props.parameter_unit,
        ens_select: this.props.ens_select || "",
        color_map: this.props.color_map,
        kind: this.props.kind as LayerKindName,
        vertical_interpolation: this.props.vertical_interpolation || false,
        calibrated: this.props.calibrated || false,
        init_date: customInitDate || undefined,
        api_url_suffix: customApiUrlSuffix || undefined,
        isAnimating: transitionRuntime.isAnimating,
        animationFps: transitionRuntime.animationFps,
        isRecording: transitionRuntime.isRecording,
        isScrubbing: transitionRuntime.isScrubbing,
      };
    });

    this.renderer = new WMTSRenderer(gl, this.publicSceneApi, this.interpolationMode);
  }

  prerender(gl: WebGL2RenderingContext, matrix: number[]): void {
    if (!this.renderer || !this.tileManager || !this.props.show || this.props.opacity <= 0.0) {
      return;
    }

    const tileSets = this.tileManager.getRenderableTileSets();
    this.renderer.prerender(gl, matrix, tileSets);
  }

  render(gl: WebGL2RenderingContext, matrix?: number[]): void {
    if (!this.renderer || !this.props.show || this.props.opacity <= 0.0) {
      return;
    }
    this.renderer.render(gl, this.props.opacity);
  }

  beforeRender(): void {
    if (!this.tileManager || !this.props.show || this.props.opacity <= 0.0) {
      this.removeDebugTileGridLayer();
      return;
    }

    this.syncDebugTileGrid();
    // isRebuffering() applies the map's time offset internally; pass the raw display time
    // here, or the offset would be applied twice and corrupt the tile-selection state.
    const nowPending = this.isRebuffering(this.scene.getDisplayTime());
    if (this.isPending.changed(nowPending)) {
      if (this.props?.id != null) {
        reportLayerLoading(this.props.id, this.isPending.value ?? false);
      }
    }
  }

  fetchData(): VoidAsyncResult {
    return createResolvedAsyncResult(undefined);
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
        ensSelect: this.props.ens_select,
      },
    ];
  }

  removeLayer(): void {
    this.removeDebugTileGridLayer();
    this.scene.getMapboxMap().removeLayer(this.id);
    if (this.props?.id != null) {
      clearLayerLoading(this.props.id);
    }
  }

  handleMapMoveStart = () => {
    this.shouldPreload = true;
    this.lastMousePos = undefined;

    this.repository?.cancelUnneededRequests();
  };

  handleMapMoveEnd = () => {
    this.shouldPreload = false;
    this.lastMousePos = undefined;

    this.repository?.cancelUnneededRequests();
  };

  private handleRawMouseMove = (e: MouseEvent) => {
    if (!this.shouldPreload || !this.tileManager) {
      return;
    }

    // Only track while dragging
    if (e.buttons !== 1) {
      return;
    }

    const currentMousePos = { x: e.clientX, y: e.clientY };

    if (this.lastMousePos) {
      const dx = currentMousePos.x - this.lastMousePos.x;
      const dy = currentMousePos.y - this.lastMousePos.y;

      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        this.tileManager.preloadTilesInPanDirection(dx, dy);
      }
    }

    this.lastMousePos = currentMousePos;
  };

  private syncDebugTileGrid(): void {
    const debugTiles = this.getDebugTilesFromViewport();
    const map = this.scene.getMapboxMap();
    this.requestedTileGridOverlay.sync(map, debugTiles, isWmtsTileBoundariesDebugEnabled());
  }

  private getDebugTilesFromViewport(): WmtsDebugTile[] {
    const viewport = this.scene.getMapViewport(0);
    const currentZoom = viewport.zoom;
    const maxDataZoom = 11;
    const tileZoom = Math.min(Math.floor(currentZoom), maxDataZoom);
    const tileViewport = this.scene.getMapViewport(Math.max(0, currentZoom - tileZoom));
    const area = tileViewport.viewportArea;
    const optimalTilesWorldZero = wrapIntoWorldZero(area);

    const debugTiles: WmtsDebugTile[] = [];
    for (const tileGeometry of tileGeometries(...optimalTilesWorldZero)) {
      const maxCoord = 2 ** tileGeometry.z - 1;
      if (tileGeometry.x < 0 || tileGeometry.x > maxCoord || tileGeometry.y < 0 || tileGeometry.y > maxCoord) {
        continue;
      }
      debugTiles.push({ x: tileGeometry.x, y: tileGeometry.y, z: tileGeometry.z });
    }

    return debugTiles;
  }

  private removeDebugTileGridLayer(): void {
    const map = this.scene.getMapboxMap();
    this.requestedTileGridOverlay.remove(map);
  }
}
